import React, { useState, useEffect } from "react";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";
import { Outlet, useLocation, useParams } from "react-router-dom";
import MuiAppBar from "@mui/material/AppBar";
import {
  Box,
  Button,
  Drawer,
  CssBaseline,
  IconButton,
  InputAdornment,
  Toolbar,
  TextField,
  Switch,
  Typography,
  Grid,
  createSvgIcon,
  Link,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import logo from "./static/logo.png";
import "./common.css";
import { useNavigate } from "react-router-dom";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import TextsmsOutlinedIcon from "@mui/icons-material/TextsmsOutlined";
import EditNoteIcon from '@mui/icons-material/EditNote';
import CancelOutlined from "@mui/icons-material/CancelOutlined";

import UserLogin from "./components/UserLogin";
import History from "./components/History";
import UserNav from "./components/UserNav";
import Results from "./components/Results";
import DateFilters from "./components/DateFilters";

import ScrollTrigger from "gsap/ScrollTrigger";

import { useSelector, useDispatch } from "react-redux";
import { updatePrompts } from "./redux/promptSlice";
import {
  updateLoading,
  updateQuery,
  updateQueryType,
} from "./redux/querySlice";
import { setShowFilters } from "./redux/dateRangeSlice";
import socket from "./constants/SocketProvider";
import { updateChatID } from "./redux/threadSlice";
import DebugPanel from "./components/Debug";
import { appName } from "./constants/urls";
import { Link as RouterLink } from "react-router-dom";
import {
  DataSaverOnOutlined,
  FilterAlt,
  HistoryOutlined,
  Person,
  Search,
  SearchOutlined,
} from "@mui/icons-material";
import { terminateWebSocket } from "./redux/webSocketSlice";

const drawerWidth = 300;
const token = sessionStorage.getItem("token");
const sessionMode = sessionStorage.getItem("mode");
const CloseSidebarIcon = createSvgIcon(
  <svg
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="m20 24h-16c-2.2 0-4-1.8-4-4v-16c0-2.2 1.8-4 4-4h16c2.2 0 4 1.8 4 4v16c0 2.2-1.8 4-4 4zm-16-22c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2v-16c0-1.1-.9-2-2-2z" />
    <path d="m8 24c-.6 0-1-.4-1-1v-22c0-.6.4-1 1-1s1 .4 1 1v22c0 .6-.4 1-1 1z" />
    <path d="m14 13c-.3 0-.5-.1-.7-.3-.4-.4-.4-1 0-1.4l3-3c.4-.4 1-.4 1.4 0s.4 1 0 1.4l-3 3c-.2.2-.4.3-.7.3z" />
    <path d="m17 16c-.3 0-.5-.1-.7-.3l-3-3c-.4-.4-.4-1 0-1.4s1-.4 1.4 0l3 3c.4.4.4 1 0 1.4-.2.2-.4.3-.7.3z" />
  </svg>,
  "CloseSidebar"
);

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    minHeight: "calc(100vh)",
    overflowAnchor: "none",
    // overflow: 'hidden',
    position: "relative",
    // background: '#EEF7FF',
    // padding: window.innerWidth > 600 ? "0 24px 24px" : theme.spacing(2),
    padding: window.innerWidth > 600 ? "0" : theme.spacing(2),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: window.innerWidth > 600 ? `-${drawerWidth}px` : 0,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: window.innerWidth > 600 ? `-${drawerWidth}px` : 0,
    }),
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    // width: `calc(100% - ${drawerWidth}px)`,
    // marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  // padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const MobileQueryPlaceholder = () => {
  const queryLoading = useSelector((state) => state.queryText.loading);
  if (queryLoading) {
    return (
      <p className="placeholder">
        <span className="placeholder-highlight">Building your stories...</span>
      </p>
    );
  } else {
    return <p className="placeholder">Type your query...</p>;
  }
};

export default function Layout() {
  const location = useLocation();

  const isSideBarEnabled = process.env.REACT_APP_IS_SIDEBAR_ENABLED;
  const isChatEnabled = process.env.REACT_APP_IS_CHAT_ENABLED;
  const isFilterEnabled = process.env.REACT_APP_IS_FILTER_ENABLED;

  const queryTextRedux = useSelector((state) => state.queryText.value);
  const queryLoading = useSelector((state) => state.queryText.loading);

  const activityRedux = useSelector((state) => state.activity.activity);
  const loadingHistoryRedux = useSelector(
    (state) => state.activity.loadingHistory
  );
  const historyID = useSelector((state) => state.historicalChatID.value);

  const startTimeRedux = useSelector((state) => state.dateRange.startDate);
  const endTimeRedux = useSelector((state) => state.dateRange.endDate);
  const showFilters = useSelector((state) => state.dateRange.showFilters);
  const prompts = useSelector((state) => state.prompts.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const thisPath = window.location.pathname;
  const thisPathSegments = thisPath.split("/").filter((segment) => segment);

  const [zeroState, setZeroState] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const [mini, setMini] = React.useState(false);
  const [queryText, setQueryText] = React.useState("");
  const [firstQuery, setFirstQuery] = React.useState(false);
  const [resultsList, setResultsList] = React.useState([]);
  const [mode, setMode] = React.useState(sessionMode || "dark");
  const [openDevPanel, setOpenDevPanel] = React.useState(false);
  const [openAltClipsPanel, setOpenAltClipsPanel] = React.useState(false);
  const [altClipsDrawerContent, setAltClipsDrawerContent] = React.useState([]);
  const [openDebugPanel, setOpenDebugPanel] = React.useState(false);
  const [debugDrawerContent, setDebugDrawerContent] = React.useState([]);
  const [validError, setValidError] = React.useState(false);

  const QueryPlaceholder = () => {
    const queryLoading = useSelector((state) => state.queryText.loading);
    const progressMessage = useSelector((state) => state.queryText.progressMessage);
    const username = sessionStorage.getItem("username")?.split("@")[0];
    if (queryLoading && queryTextRedux?.length > 0) {
      return (
        <p
          className="placeholder placeholder-highlight"
          style={{
            maxWidth: "92%",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: theme.palette.primary.main,
            fontWeight: 500,
          }}
        >
          {progressMessage || "Loading..."}
          {/* {queryTextRedux} */}
        </p>
      );
    } else {
      return (
        <p className="placeholder" sx={{ marginLeft: "1rem" }}>
          {username && (
            <span className="placeholder-highlight">
              {/* Hi {username.charAt(0).toUpperCase() + username.slice(1)} */}
              Hi {username.charAt(0).toUpperCase() + username.slice(1) + ","}
            </span>
          )}
          What story would you like to make?
        </p>
      );
    }
  };

  const handleCancelWebSocket = () => {
    dispatch(updateLoading(false));
    dispatch(terminateWebSocket());
  };

  const handlePromptChange = (promptName, e) => {
    let currPrompts = { ...prompts };
    currPrompts[promptName] = e.target.value;
    dispatch(updatePrompts(currPrompts));
  };

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const handleDrawerClose = () => {
    setOpen(!open);
  };

  const handleDrawerMinimize = () => {
    setMini(!mini);
  };

  const handleDevPanelClose = () => {
    setOpenDevPanel(false);
  };

  const handleAltCLipsPanelClose = () => {
    setOpenAltClipsPanel(false);
  };

  const handleDebugPanelClose = () => {
    setOpenDebugPanel(false);
  };

  const handleFilterPanel = () => {
    dispatch(setShowFilters(!showFilters));
  };

  const messagesEndRef = React.useRef();

  const scrollToBottom = () => {
    let chatBottom = document.getElementById("chatboxBottom");
    if (chatBottom) {
      window.setTimeout(() => {
        chatBottom.scrollIntoView({ behavior: "smooth", block: "start" });
        const offset = window.innerHeight * 0.25 || 150;
        window.scrollBy({ top: -offset, behavior: "smooth" });
      }, 0);
    }
  };

  const scrollToTop = () => {
    var evt = document.createEvent("MouseEvents");
    evt.initEvent("wheel", true, true);
    evt.deltaY = +120;
    evt.deltaY = -120;
    document.getElementById("main-container").dispatchEvent(evt);
  };

  const handleChatReset = () => {
    // setResultsList([]);
    // dispatch(updateQuery(""));
    dispatch(updateChatID(""));
    if (queryTextRedux) {
      navigate("/");
      window.location.reload();
    }
  };

  const handleQuery = () => {
    dispatch(updateQueryType("chatQuery"));
    if (
      queryTextRedux.length > 0 &&
      thisPathSegments[thisPathSegments.length - 1] === "story"
    ) {
      setFirstQuery(false);
      dispatch(updateLoading(true));
      dispatch(updateQuery(queryText));
      // sendToResults();
      setQueryText("");
    } else {
      setFirstQuery(true);
      dispatch(updateLoading(true));
      dispatch(updateQuery(queryText));
      setQueryText("");
    }
  };

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: mode,
          ...(mode === "light"
            ? {
              background: { default: "#EEF7FF" },
            }
            : {
              // fallback palette values for dark mode
              background: { default: "#191D25" },
            }),
          primary: {
            main: "#ac1fff",
            light: "#B588FF",
            dark: "#0A0118",
            white: "#FFFFFF",
            gray: "#777777",
            darkGray: "#999999",
          },
        },
        typography: {
          fontFamily: '"Plus Jakarta Sans", sans-serif', // Set Plus Jakarta Sans globally
        },
        customBackground: {
          gradient:
            "radial-gradient(circle, rgba(113, 61, 255, 0.2) 0%, rgba(113, 61, 255, 0) 100%), radial-gradient(circle, rgba(113, 61, 255, 0.06) 0%, rgba(113, 61, 255, 0) 100%)",
          // gradient:
          //   "radial-gradient(rgba(113, 61, 255, 0.2), rgba(113, 61, 255, 0), rgba(113, 61, 255, 0.06), rgba(113, 61, 255, 0))",
        },
      }),
    [mode]
  );

  const sendToResults = () => {
    if (thisPathSegments[thisPathSegments.length - 1] !== "story") {
      // navigate("/q/" + encodeURIComponent(queryTextRedux) + "/story");
    }
    if (queryTextRedux.length !== "" && activityRedux.length === 0) {
      setZeroState(false);
      if (resultsList.length > 0 && resultsList[0].props.queryText == "") {
        setResultsList([
          <Results
            queryText={queryTextRedux}
            isHistory={activityRedux.length > 0}
            theme={mode}
            open_alt_clips_panel={setOpenAltClipsPanel}
            alt_clips_drawer={setAltClipsDrawerContent}
            open_debug_panel={setOpenDebugPanel}
            debug_drawer={setDebugDrawerContent}
          />,
        ]);
      } else {
        setResultsList(
          resultsList.concat(
            <Results
              queryText={queryTextRedux}
              isHistory={activityRedux.length > 0}
              theme={mode}
              open_alt_clips_panel={setOpenAltClipsPanel}
              alt_clips_drawer={setAltClipsDrawerContent}
              open_debug_panel={setOpenDebugPanel}
              debug_drawer={setDebugDrawerContent}
            />
          )
        );
      }
    } else if (activityRedux.length > 0) {
      console.log("activityredux.length > 0");
      setZeroState(false);
      setResultsList([
        <Results
          queryText={queryTextRedux}
          isHistory={true}
          chatID={historyID}
          addToHistory={true}
          activityLog={activityRedux}
          theme={mode}
          open_alt_clips_panel={setOpenAltClipsPanel}
          alt_clips_drawer={setAltClipsDrawerContent}
          open_debug_panel={setOpenDebugPanel}
          debug_drawer={setDebugDrawerContent}
        />,
      ]);
    } else {
      setZeroState(false);
      console.log("oi oi, we are adding to history");
      if (resultsList.length > 0) {
        setResultsList([
          <Results
            queryText={queryTextRedux}
            isHistory={false}
            theme={mode}
            open_alt_clips_panel={setOpenAltClipsPanel}
            alt_clips_drawer={setAltClipsDrawerContent}
            open_debug_panel={setOpenDebugPanel}
            debug_drawer={setDebugDrawerContent}
          />,
        ]);
      } else {
        setResultsList(
          resultsList.concat(
            <Results
              queryText={queryTextRedux}
              isHistory={false}
              theme={mode}
            />
          )
        );
      }
    }
  };

  useEffect(() => {
    const smith = document.getElementById("ask-smith");
    if (smith) {
      smith.value = "";
      // scrollToBottom();
    }

    if (queryText.length > 0 || queryTextRedux.length > 0) {
      setZeroState(false);
      // scrollToBottom();
      sendToResults();
      //   setResultsList(resultsList.concat(<Results queryText={queryTextRedux} isHistory={false} theme={mode} />));
    }

    // if (activityRedux.length > 0) {
    //   console.log("adding to activity with chatID: ", historyID)
    //   setResultsList(<Results queryText={queryTextRedux} isHistory={activityRedux.length > 0} addToHistory={true} chatID={historyID} activityLog={activityRedux} theme={mode}/>)
    // }

    if (
      queryTextRedux.length > 1 &&
      activityRedux.length > 1 &&
      historyID.length > 1
    ) {
      setResultsList([
        <Results
          queryText={queryTextRedux}
          isHistory={activityRedux.length > 0}
          chatID={historyID}
          addToHistory={true}
          activityLog={activityRedux}
          theme={mode}
          open_alt_clips_panel={setOpenAltClipsPanel}
          alt_clips_drawer={setAltClipsDrawerContent}
          open_debug_panel={setOpenDebugPanel}
          debug_drawer={setDebugDrawerContent}
        />,
      ]);
    }
    if (firstQuery) {
      scrollToTop();
    } else {
      scrollToBottom();
    }
  }, [queryTextRedux]);

  // useEffect(() => {
  //   // console.log("historical chat ID is now: ", historyID)
  //   setResultsList([]);
  // }, [historyID]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          background:
            mode === "dark"
              ? theme.customBackground.gradient
              : theme.palette.background.default,
          // background: '#191D25',
        }}
      >
        <CssBaseline />
        <AppBar
          position="fixed"
          open={open}
          color="transparent"
          elevation={0}
          sx={{
            borderBottom: "1px solid #eeeeee",
            position: "fixed",
            background: theme.palette.primary.dark,
          }}
        >
          <Toolbar sx={{ padding: "0 1rem !important" }}>
            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: "2rem",
              }}
            >
              {/* Left Section */}
              <Box
                className="flex-center-between"
                p={2}
                sx={{
                  flex: 1,
                  gap: "8px",
                }}
              >
                <Box className="flex-center" gap="0.5rem">
                  <a href="/" style={{ textDecoration: "none" }}>
                    <img src={logo} width={40} height={55} alt="logo" />
                  </a>
                  <Box>
                    <a
                      href="/"
                      style={{
                        textDecoration: "none",
                        color: theme.palette.primary.main,
                        display: { xs: "none", sm: "none" }, // Hidden on mobile
                        display: "block",
                      }}
                    >
                      <Typography fontWeight={700} fontSize={"1.2rem"}>
                        ShortTok{" "}
                      </Typography>
                      <Typography fontSize={"0.8rem"}>{appName}</Typography>
                    </a>
                  </Box>
                </Box>
                {/* {location?.pathname !== "/" &&
                  location?.pathname !== "/user/login" &&
                  false && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={token ? false : true}
                      onClick={handleChatReset}
                      startIcon={
                        !token ? (
                          <LockPersonOutlinedIcon />
                        ) : (
                          <AddCircleOutlineIcon />
                        )
                      }
                      sx={{
                        display: { xs: "none", sm: "flex", md: "flex" },
                        "&:hover": {
                          background: theme.palette.primary.main,
                          color: "#FFFFFF",
                        },
                        borderRadius: 12,
                        fontSize: { xs: 0, sm: 12 },
                        fontWeight: 700,
                        boxShadow: 0,
                        background: theme.palette.primary.main,
                        color: "#FFFFFF",
                        textTransform: "capitalize",
                        p: { xs: 1 },
                      }}
                    >
                      New Story
                    </Button>
                  )} */}
                <div>
                </div>
                {token && token !== "undefined" && (
                  <Tooltip title="New Session" arrow>
                    <IconButton
                      onClick={handleChatReset}
                      sx={{
                        borderRadius: "8px",
                        marginLeft: "1rem",
                        border: "1px solid #A9A3C233",
                        background: theme.palette.primary.dark,
                        color: theme.palette.primary.white,
                      }}
                    >
                      <EditNoteIcon fontSize="large" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {/* Middle Section */}
              <Box
                className="flex-center"
                // p={2}
                flex={2}
              >
                {token && token !== "undefined" && (
                  <Box className="flex-center" width="100%">
                    <div className="fancyForm">
                      <TextField
                        id="ask-smith"
                        variant="outlined"
                        // disabled={queryLoading || isChatEnabled !== "true"}
                        disabled={queryLoading}
                        required
                        error={validError}
                        helperText={
                          validError
                            ? "Try adding some text to your query!"
                            : " "
                        }
                        fullWidth
                        onChange={(e) => setQueryText(e.target.value)}
                        onSubmit={handleQuery}
                        onKeyUp={(e) => {
                          if (e?.key === "Enter" && queryText.length > 0) {
                            handleQuery();
                          }
                        }}
                        InputProps={{
                          sx: {
                            cursor: queryLoading ? "not-allowed" : "text",
                            caretColor: queryLoading ? "red" : "auto",
                            background:
                              mode === "light" ? "#2d3330" : "#121212",
                            color: "#ffffff",
                            padding: "4px 8px",
                            // borderRadius: 2,
                            input: {
                              padding: "12px 16px 12px 0",
                              fontSize: { xs: 12, sm: 14 },
                              "&::placeholder": {
                                opacity: 1,
                                fontSize: { xs: 12, sm: 14 },
                                fontWeight: 400,
                              },
                            },
                          },
                          startAdornment: (
                            <InputAdornment position="start">
                              {queryLoading ?
                                <CircularProgress
                                  size={18}
                                  thickness={8}
                                  sx={{
                                    marginRight: "12px",
                                    verticalAlign: "middle",
                                    color: "#AC1FFF",
                                  }}
                                /> : <SearchOutlined
                                  sx={{
                                    fontSize: "24px",
                                    color: theme.palette.primary.main,
                                  }}
                                />}

                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="start">
                              {queryText.length > 0 && (
                                <IconButton
                                  disabled={queryLoading}
                                  onClick={handleQuery}
                                  sx={{
                                    "&:hover": {
                                      background: "#ffffff",
                                      backgroundColor: theme.palette.primary.main,
                                    },
                                    backgroundColor: theme.palette.primary.main,
                                    color: "#ffffff",
                                  }}
                                >
                                  <ArrowForwardIcon fontSize="small" />
                                </IconButton>
                              )}
                              {queryLoading &&
                                <Tooltip title="Stop loading" arrow>
                                  <CancelOutlined
                                    size={18}
                                    thickness={8}
                                    sx={{
                                      marginLeft: "12px",
                                      verticalAlign: "middle",
                                      color: "#AC1FFF",
                                      cursor: "pointer",
                                    }}
                                    onClick={handleCancelWebSocket}
                                  />
                                </Tooltip>}
                            </InputAdornment>
                          ),
                        }}
                      />
                      {window.innerWidth < 600 ? (
                        <MobileQueryPlaceholder />
                      ) : (
                        <QueryPlaceholder />
                      )}
                    </div>
                    {/* {queryLoading && (
                      <IconButton>
                        <DataSaverOnOutlined fontSize="large" />
                      </IconButton>
                    )} */}
                  </Box>
                )}
              </Box>
              {/* Right Section */}
              {token && token !== "undefined" && (
                <Box
                  className="flex-center-end"
                  p={2}
                  sx={{
                    flex: 1,
                    gap: "2rem",
                  }}
                >
                  <Tooltip title="History" arrow>
                    <IconButton
                      aria-label="open drawer"
                      onClick={handleDrawerOpen}
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #A9A3C233",
                        background: theme.palette.primary.dark,
                        color: theme.palette.primary.white,
                      }}
                    >
                      <HistoryOutlined />
                    </IconButton>
                  </Tooltip>
                  {/* <IconButton
                    sx={{
                      borderRadius: "8px",
                      border: "1px solid #A9A3C233",
                      background: theme.palette.primary.dark,
                      color: theme.palette.primary.main,
                    }}
                    onClick={handleFilterPanel}
                  >
                    <FilterAlt />
                  </IconButton> */}

                  <UserNav openDevPanel={setOpenDevPanel} />
                </Box>
              )}
            </Box>
          </Toolbar>
        </AppBar>
        <Drawer
          PaperProps={{
            sx: {
              backgroundColor: mode === "light" ? "#ffffff" : "#000000",
              border: 0,
              padding: "0 24px 24px",
              margin: "60px 12px 12px",
              borderRadius: "12px",
              height: "calc(100% - 84px)",
              boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
              width: mini ? "64px" : drawerWidth,
            },
          }}
          sx={{
            width: window.innerWidth < 600 ? 0 : drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: mini ? "64px" : drawerWidth,
              boxSizing: "border-box",
            },
          }}
          variant="persistent"
          anchor="left"
          open={open}
        >
          <DrawerHeader>
            <IconButton
              sx={{ marginRight: -2 }}
              onClick={handleDrawerClose} //{handleDrawerMinimize} //{handleDrawerMinimize}
            >
              {theme.direction === "ltr" ? (
                <CloseSidebarIcon style={{ fill: "#999", width: 18 }} />
              ) : (
                <ChevronRightIcon />
              )}
            </IconButton>
          </DrawerHeader>

          {token && token !== "undefined" && <History mini={mini} />}
          {/*
        <Box sx={{display: "flex"}}>
          <SettingsOutlined /> Settings
        </Box> */}

          {!mini && (
            <Box sx={{ display: "flex", fontSize: 14, marginTop: 2 }}>
              Dark Mode:
              <Switch
                checked={mode === "light" ? false : true}
                size="small"
                onChange={() => {
                  setMode(mode === "light" ? "dark" : "light");
                  sessionStorage.setItem(
                    "mode",
                    mode === "light" ? "dark" : "light"
                  );
                }}
              />
            </Box>
          )}

          {/* {token && token !== 'undefined' &&  (
            <AdminNav />
        )} */}
        </Drawer>
        <Main open={open} id="main-container">
          <DrawerHeader />

          {(token === "undefined" || !token) && (
            <UserLogin />
            // <Outlet />
          )}

          {/* {thisPathSegments.length > 1 &&
            !zeroState &&
            thisPathSegments[thisPathSegments.length - 1] === "storylines" && (
              <Grid container>
                <Grid item sm={12} md={1} lg={3}></Grid>
                <Grid
                  item
                  sm={12}
                  md={10}
                  lg={6}
                  style={{ position: "relative" }}
                >
                  <Outlet />
                </Grid>
                <Grid item sm={12} md={1} lg={3}></Grid>
              </Grid>
              // <Feed theme={mode} />
            )} */}

          {thisPathSegments.length < 1 &&
            thisPathSegments[thisPathSegments.length - 1] === "feed" && (
              <Outlet theme={mode} />
              // <Feed theme={mode} />
            )}

          {/* {token && token !== "undefined" && zeroState && ( */}
          {token && token !== "undefined" && (
            // window.location.pathname !== "/feed" &&
            <div>
              <Grid container>
                {/* <Grid item sm={12} md={1} lg={3}></Grid> */}
                <Grid
                  item
                  sm={12}
                  md={12}
                  lg={12}
                  style={{ position: "relative" }}
                >
                  <Outlet />
                </Grid>
                {/* <Grid item sm={12} md={1} lg={3}></Grid> */}
              </Grid>
            </div>
          )}

          {token &&
            token !== "undefined" &&
            (!zeroState || activityRedux.length !== 0) &&
            thisPathSegments[thisPathSegments.length - 1] !== "storylines" &&
            thisPath !== "/" && (
              <Grid container className="test">
                {/* <Grid item sm={12} md={2} sx={{ textAlign: "right" }}> */}
                {/* <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setResultsList([]);
                      dispatch(updateQuery(""));
                      handleChatReset();
                    }}
                    startIcon={<KeyboardBackspaceIcon />}
                    sx={{
                      color: "#AC1FFF",
                      borderColor: "#AC1FFF",
                      borderRadius: 50,
                      fontSize: 10,
                      marginBottom: 4,
                      marginRight: 2,
                      "&:hover": {
                        background: "#AC1FFF",
                        color: "#FFFFFF",
                        borderColor: "#AC1FFF",
                      },
                    }}
                  >
                    Back
                  </Button> */}
                {/* </Grid> */}
                <Grid item sm={12} md={12} id="resultsBox">
                  {/* {resultsList} */}
                  <div ref={messagesEndRef} id="chatboxBottom" />
                </Grid>
                {/* <Grid item sm={12} md={2}></Grid> */}
              </Grid>
            )}

          {startTimeRedux && endTimeRedux && (
            <div className="datefilteralert" onClick={handleFilterPanel}>
              Date filters:{" "}
              {new Date(startTimeRedux).toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
              })}{" "}
              -{" "}
              {new Date(endTimeRedux).toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric",
              })}
            </div>
          )}
        </Main>
        <DebugPanel
          openDebugPanel={openDebugPanel}
          handleDebugPanelClose={handleDebugPanelClose}
          debugDrawerContent={debugDrawerContent}
        />
        <Drawer
          open={openAltClipsPanel}
          anchor="right"
          PaperProps={{
            sx: {
              backgroundColor:
                sessionStorage.getItem("mode") === "light"
                  ? "#FFFFFF"
                  : "#000000",
              border: 0,
              margin: "60px 12px 12px",
              borderRadius: "12px",
              height: "calc(100% - 84px)",
              boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
              // width: mini ? '64px' : drawerWidth,
            },
          }}
          sx={{
            width: window.innerWidth * 0.33,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width:
                window.innerWidth < 600
                  ? "calc(100% - 24px)"
                  : window.innerWidth * 0.33,
              boxSizing: "border-box",
            },
          }}
        >
          <DrawerHeader
            sx={{
              position: "fixed",
              background: "#fff",
              borderRadius: "24px 24px 0 0",
              width: "inherit",
              margin: 0,
              borderBottom: "1px solid #ccc",
              zIndex: 999,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                margin: 2,
                fontFamily: "Roboto Condensed",
                fontWeight: 600,
                width: "100%",
              }}
            >
              <VideoLibraryOutlinedIcon
                sx={{ mr: 2, fontSize: 24, verticalAlign: "middle" }}
              />
              Alternate Clips
            </Typography>
            <IconButton onClick={handleAltCLipsPanelClose}>
              {<CloseIcon />}
            </IconButton>
          </DrawerHeader>
          <div style={{ paddingTop: 36 }}>{altClipsDrawerContent}</div>
        </Drawer>
        <Drawer
          open={showFilters}
          anchor="right"
          PaperProps={{
            sx: {
              backgroundColor:
                sessionStorage.getItem("mode") === "light"
                  ? "#FFFFFF"
                  : "#000000",
              color:
                sessionStorage.getItem("mode") === "light"
                  ? "#000000"
                  : "#FFFFFF",
              border: 0,
              margin: "60px 12px 12px",
              borderRadius: "12px",
              height: "calc(100% - 84px)",
              boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
              // width: mini ? '64px' : drawerWidth,
            },
          }}
          sx={{
            width: window.innerWidth * 0.23,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width:
                window.innerWidth < 600
                  ? "calc(100% - 24px)"
                  : window.innerWidth * 0.23,
              boxSizing: "border-box",
            },
          }}
        >
          <DrawerHeader
            sx={{
              position: "fixed",
              backgroundColor:
                sessionStorage.getItem("mode") === "light"
                  ? "#FFFFFF"
                  : "#000000",
              color:
                sessionStorage.getItem("mode") === "light"
                  ? "#000000"
                  : "#FFFFFF",
              borderRadius: "24px 24px 0 0",
              width: "inherit",
              margin: 0,
              borderBottom: "1px solid #ccc",
              zIndex: 999,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                margin: 2,
                fontFamily: "Roboto Condensed",
                fontWeight: 600,
                width: "100%",
              }}
            >
              <TuneIcon sx={{ mr: 2, fontSize: 24, verticalAlign: "middle" }} />
              Filters
            </Typography>
            <IconButton onClick={handleFilterPanel}>{<CloseIcon />}</IconButton>
          </DrawerHeader>
          <div style={{ paddingTop: 70 }} />
          <DateFilters />
        </Drawer>
      </Box>
    </ThemeProvider>
  );
}
